import React from 'react';
import { Phone, CheckCircle, AlertTriangle, Gauge, Wrench, TrendingUp, DollarSign } from 'lucide-react';
export function FuelSavings() {
  return <div className="min-h-screen bg-gray-50" data-id="element-1396">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-1397">
        <div className="container mx-auto px-4" data-id="element-1398">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-1399">
            Fuel Savings Tips
          </h1>
          <p className="text-xl text-gray-100 italic" data-id="element-1400">
            Serving Farmington Hills, Farmington, Novi, Livonia, Northville
            Since 1958
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-1401">
        <div className="max-w-4xl mx-auto" data-id="element-1402">
          {/* Introduction */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1403">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-1404">
              It's easier than you think to improve!
            </h2>

            <p className="text-lg text-gray-700 mb-6" data-id="element-1405">
              Many factors contribute to the fuel efficiency you may be getting
              in your vehicle.
            </p>

            <p className="text-lg text-gray-700 mb-6" data-id="element-1406">
              Follow these guidelines* and see an increase in engine performance
              and gas mileage.
            </p>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-6" data-id="element-1407">
              <p className="text-gray-800 mb-4" data-id="element-1408">
                Virtually all of your vehicle's mechanical systems can affect
                fuel efficiency if not properly maintained. Follow the
                manufacturer's recommendations for checking the engine, cooling
                and ignition system, brakes, drivetrain and emission-control
                system.
              </p>
              <p className="text-gray-800 font-semibold" data-id="element-1409">
                A poorly maintained vehicle can boost fuel consumption by up to
                15% and greenhouse gas emissions by even more!
              </p>
            </div>
          </div>

          {/* Items to Check */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1410">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-1411">
              ITEMS TO CHECK AND/OR CHANGE REGULARLY INCLUDE...
            </h2>

            <div className="space-y-6" data-id="element-1412">
              {/* Tire Pressure */}
              <div className="border-l-4 border-[#B1050B] pl-6" data-id="element-1413">
                <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center" data-id="element-1414">
                  <Gauge className="mr-2 text-[#B1050B]" size={24} data-id="element-1415" />
                  TIRE PRESSURE
                </h3>
                <p className="text-gray-700" data-id="element-1416">
                  Check tires regularly, under inflated tires can reduce your
                  gas mileage up to <strong data-id="element-1417">10%</strong>.
                </p>
              </div>

              {/* Air Filter */}
              <div className="border-l-4 border-[#B1050B] pl-6" data-id="element-1418">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1419">
                  AIR FILTER
                </h3>
                <p className="text-gray-700 mb-2" data-id="element-1420">
                  A dirty or clogged air filter will result in lower fuel
                  economy and increased engine wear.
                </p>
                <p className="text-gray-700 font-semibold" data-id="element-1421">
                  A dirty filter can reduce your gas mileage up to{' '}
                  <strong data-id="element-1422">20%</strong>.
                </p>
              </div>

              {/* Spark Plugs */}
              <div className="border-l-4 border-[#B1050B] pl-6" data-id="element-1423">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1424">
                  SPARK PLUGS
                </h3>
                <p className="text-gray-700" data-id="element-1425">
                  Worn or fouled spark plugs will cause your engine to perform
                  poorly and reduce gas mileage as much as <strong data-id="element-1426">12%</strong>.
                </p>
              </div>

              {/* Spark Plug Wires */}
              <div className="border-l-4 border-[#B1050B] pl-6" data-id="element-1427">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1428">
                  SPARK PLUG WIRES
                </h3>
                <p className="text-gray-700" data-id="element-1429">
                  Replace wires for optimal performance. Change your wires along
                  with your spark plugs.
                </p>
              </div>

              {/* Distributor Cap */}
              <div className="border-l-4 border-[#B1050B] pl-6" data-id="element-1430">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1431">
                  DISTRIBUTOR CAP
                </h3>
                <p className="text-gray-700" data-id="element-1432">
                  A worn or cracked distributor cap or rotor will reduce the
                  performance of your engine and waste gas. Inspect them during
                  tune-up, replace if corroded or worn.
                </p>
              </div>

              {/* Oxygen Sensor */}
              <div className="border-l-4 border-[#B1050B] pl-6" data-id="element-1433">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1434">
                  OXYGEN SENSOR
                </h3>
                <p className="text-gray-700" data-id="element-1435">
                  A slow or faulty oxygen sensor will cause a drop in fuel
                  economy of <strong data-id="element-1436">10-15%</strong>.
                </p>
              </div>

              {/* Fuel Filter */}
              <div className="border-l-4 border-[#B1050B] pl-6" data-id="element-1437">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1438">
                  FUEL FILTER
                </h3>
                <p className="text-gray-700" data-id="element-1439">
                  Clogged or restricted fuel filters not only reduce engine
                  performance but can allow dirt to contaminate the fuel system
                  and cause engine wear. Most manufacturers recommend
                  replacement every 3 years or 36,000 miles.
                </p>
              </div>

              {/* Fuel Injector Cleaner */}
              <div className="border-l-4 border-[#B1050B] pl-6" data-id="element-1440">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1441">
                  FUEL INJECTOR CLEANER
                </h3>
                <p className="text-gray-700" data-id="element-1442">
                  Clean fuel injectors perform better and make your engine more
                  efficient.
                </p>
              </div>

              {/* Air Intake Cleaner */}
              <div className="border-l-4 border-[#B1050B] pl-6" data-id="element-1443">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1444">
                  AIR INTAKE CLEANER
                </h3>
                <p className="text-gray-700" data-id="element-1445">
                  Make sure your throttle body is clean with no build-up. On
                  engines equipped with a carburetor, make sure the choke
                  linkage is clean and operating properly.
                </p>
              </div>

              {/* PCV Valve */}
              <div className="border-l-4 border-[#B1050B] pl-6" data-id="element-1446">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1447">
                  PCV VALVE
                </h3>
                <p className="text-gray-700" data-id="element-1448">
                  Malfunctioning PCV valve or dirty breather filters prohibit
                  your engine from breathing properly and hinder engine
                  protection.
                </p>
              </div>

              {/* Oil Change */}
              <div className="border-l-4 border-[#B1050B] pl-6" data-id="element-1449">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1450">
                  OIL CHANGE
                </h3>
                <p className="text-gray-700" data-id="element-1451">
                  Clean motor oil not only protects your engine, it can lower
                  your exhaust emissions and allow your engine to operate more
                  efficiently.
                </p>
              </div>
            </div>
          </div>

          {/* Check Engine Light */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1452">
            <div className="bg-red-50 border-2 border-red-400 rounded-lg p-6 text-center" data-id="element-1453">
              <AlertTriangle className="mx-auto text-red-600 mb-4" size={48} data-id="element-1454" />
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1455">
                MANY VEHICLES HAVE THE "CHECK ENGINE" LIGHT ON!
              </h2>
              <p className="text-xl text-gray-700 font-semibold" data-id="element-1456">
                Don't let this cost you gas mileage or reduced performance!
              </p>
            </div>
          </div>

          {/* Driving Tips */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1457">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-1458">
              Driving Tips for Better Fuel Economy
            </h2>

            <div className="space-y-6" data-id="element-1459">
              {/* Take a Load Off */}
              <div className="border-l-4 border-blue-500 pl-6" data-id="element-1460">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1461">
                  TAKE A LOAD OFF
                </h3>
                <p className="text-gray-700" data-id="element-1462">
                  Unload anything you don't need from the trunk/backseat. An
                  extra 100 pounds will reduce fuel economy by 1-2%. If you have
                  a roof rack or roof carrier, install it on your vehicle only
                  when absolutely necessary. Not only does the carrier add extra
                  weight, but it also increases the aerodynamic drag on the
                  vehicle, which further contributes to a loss of fuel economy.
                </p>
              </div>

              {/* Don't Drive Aggressively */}
              <div className="border-l-4 border-blue-500 pl-6" data-id="element-1463">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1464">
                  DON'T DRIVE AGGRESSIVELY
                </h3>
                <p className="text-gray-700" data-id="element-1465">
                  Mashing the accelerator, braking hard and speeding all
                  contribute to a decrease in fuel economy. Give yourself extra
                  time to get to your destination and think "slow and steady".
                  On surface streets, driving the speed limit will give you
                  mostly green lights, which improves your gas mileage as well
                  as reduces wear on your brakes. On the highway, the DOA says
                  that every 5 mph you drive over 65 mph represents a 7%
                  decrease in fuel economy.
                </p>
              </div>

              {/* Use Cruise Control */}
              <div className="border-l-4 border-blue-500 pl-6" data-id="element-1466">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1467">
                  USE CRUISE CONTROL - SELECTIVELY
                </h3>
                <p className="text-gray-700" data-id="element-1468">
                  Using cruise control can improve your gas mileage by helping
                  you maintain a steady speed, but only if you are driving on
                  mostly flat roads. If you are driving in hilly terrain, using
                  cruise control typically causes your vehicle to speed up
                  faster (to maintain the preset speed) than it would if you
                  were operating the accelerator yourself. Before you push that
                  cruise control button, think about the terrain ahead.
                </p>
              </div>

              {/* Avoid Excessive Idling */}
              <div className="border-l-4 border-blue-500 pl-6" data-id="element-1469">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1470">
                  AVOID EXCESSIVE IDLING
                </h3>
                <p className="text-gray-700" data-id="element-1471">
                  When a car is idling, it is using fuel, yet it is not going
                  anywhere. This translates to 0 mph. When you leave your car
                  running while waiting in line at the drive-thru, or as you
                  wait outside your child's school, you are wasting fuel. It is
                  more efficient to turn the car off, so park the car and go
                  inside!
                </p>
              </div>

              {/* Plan and Combine Trips */}
              <div className="border-l-4 border-blue-500 pl-6" data-id="element-1472">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1473">
                  PLAN AND COMBINE TRIPS
                </h3>
                <p className="text-gray-700 mb-2" data-id="element-1474">
                  A warmed-up engine is more fuel efficient than a cold one.
                  Many short trips taken from a cold start can use twice as much
                  as one multipurpose trip covering the same distance when the
                  engine is warmed up and efficient.
                </p>
                <p className="text-gray-700 font-semibold" data-id="element-1475">
                  Note: Letting your car idle to warm up doesn't help your fuel
                  economy, it actually uses more fuel and creates more
                  pollution.
                </p>
              </div>
            </div>
          </div>

          {/* Gas Buying Tips */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1476">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center flex items-center justify-center" data-id="element-1477">
              <DollarSign className="mr-2 text-[#B1050B]" size={32} data-id="element-1478" />
              Gas Buying Tips
            </h2>

            <div className="space-y-6" data-id="element-1479">
              {/* Premium vs Regular */}
              <div className="border-l-4 border-green-500 pl-6" data-id="element-1480">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1481">
                  PREMIUM- vs. REGULAR-GRADE GASOLINE
                </h3>
                <p className="text-gray-700" data-id="element-1482">
                  The recommended gasoline for most cars is regular unleaded.
                  Using a higher-octane gasoline than recommended by the owner's
                  manual does NOT improve performance OR fuel efficiency; it
                  only costs more money! Check your owner's manual to determine
                  the lowest grade of fuel that you can use.
                </p>
              </div>

              {/* Buy Gas on Wednesdays */}
              <div className="border-l-4 border-green-500 pl-6" data-id="element-1483">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1484">
                  BUY GAS ON WEDNESDAYS
                </h3>
                <p className="text-gray-700" data-id="element-1485">
                  Gas prices are statistically the cheapest on Wednesdays, but
                  this only holds true over a large number of days. It won't be
                  true every week. Gas prices often jump before holidays too.
                </p>
              </div>

              {/* Buy Gas During Cool Times */}
              <div className="border-l-4 border-green-500 pl-6" data-id="element-1486">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1487">
                  BUY GAS DURING THE COOLEST TIME OF THE DAY
                </h3>
                <p className="text-gray-700" data-id="element-1488">
                  During these times gasoline is the densest. Keep in mind - gas
                  pumps measure volumes of gasoline, not densities of fuel
                  concentration. You are charged according to 'volume of
                  measurement'.
                </p>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1489">
            <div className="bg-gray-50 border-l-4 border-gray-400 p-6" data-id="element-1490">
              <p className="text-sm text-gray-700" data-id="element-1491">
                *By following these simple guidelines and following regular
                maintenance schedules for your vehicle, you should see an
                increase in engine performance and actual gas mileage. Results
                will vary depending on individual vehicles, maintenance, model
                and other unique variables. This checklist presents suggested
                ways of enhancing gas mileage and engine performance. This does
                not guarantee results.
              </p>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-1492">
            <h2 className="text-3xl font-bold mb-4" data-id="element-1493">
              Improve Your Fuel Economy Today
            </h2>
            <p className="text-xl mb-6" data-id="element-1494">
              Schedule a maintenance checkup and start saving on gas
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-1495">
              <Phone className="mr-3" size={24} data-id="element-1496" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-1497">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}